import { Button, Flex, notification } from "antd";
import { FC, useCallback, useState } from "react";

import { BaseModal } from "@/components/BaseModal";
import { PrimaryText } from "@/components/Typography/Styled";
import { latencyIcons } from "@/constants/ui/latencyIcons";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useMultiTap } from "@/hooks/useMultiTap";
import { updateOfflineStatus } from "@/store/slices/offline";
import { isOnline } from "@/utils/network/isOnline";

import { StyledExclamationCircleOutlined } from "./Styled";
import { checkLatencyIcon } from "./utils/checkLatencyIcon";
import { formatLatency } from "./utils/formatLatency";

interface NetworkIndicatorProps {
    canChangeOfflineMode?: boolean;
}

export const NetworkIndicator: FC<NetworkIndicatorProps> = ({
    canChangeOfflineMode = false,
}) => {
    const dispatch = useAppDispatch();

    const { latency, offlineMode } = useAppSelector((state) => state.offline);
    const { type } = useAppSelector((state) => state.theme);

    const [openModal, setOpenModal] = useState(false);

    const handleOpenModal = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleDoubleTap = useMultiTap(handleOpenModal);

    const handleChangeMode = useCallback(async () => {
        if (offlineMode) {
            const online = await isOnline();

            if (!online) {
                dispatch(updateOfflineStatus(true));

                notification.error({
                    message:
                        "Відсутня мережа, додаток не зміг перейти у онлайн",
                });
            } else {
                dispatch(updateOfflineStatus(false));
            }
        } else {
            dispatch(updateOfflineStatus(true));
        }

        handleCloseModal();
    }, [dispatch, offlineMode]);

    const content = () => (
        <Flex vertical align="center" className="network-indicator">
            <PrimaryText fontSize={8} $textNoWrap lineHeight="120%">
                {latency === "offline" ? "∞" : formatLatency(latency)}
            </PrimaryText>

            {latency === "offline" ? (
                <div
                    dangerouslySetInnerHTML={{
                        __html: latencyIcons[type].off,
                    }}
                    style={{
                        lineHeight: 0.6,
                    }}
                />
            ) : (
                <img src={checkLatencyIcon(type, latency)} alt="latency-icon" />
            )}
        </Flex>
    );

    return (
        <>
            {canChangeOfflineMode ? (
                <Button
                    type="text"
                    onDoubleClick={handleOpenModal}
                    style={{ width: 32, padding: 0 }}
                    onTouchEnd={handleDoubleTap}
                >
                    {content()}
                </Button>
            ) : (
                content()
            )}

            <BaseModal
                onClose={handleCloseModal}
                open={openModal}
                title={
                    offlineMode ? "Повернутися в онлайн" : "Перейти в офлайн"
                }
                icon={
                    <StyledExclamationCircleOutlined style={{ fontSize: 24 }} />
                }
                maskClosable
                description={
                    offlineMode
                        ? "Повернемо синхронізацію даних із сервером."
                        : "Дані будуть зберігатися локально та синхронізуються, коли зʼявиться інтернет."
                }
                onConfirm={handleChangeMode}
            />
        </>
    );
};
